import React, { useState } from 'react';
import {
  X,
  Calendar,
  CheckCircle,
  Landmark,
  Coins,
  AlertCircle,
  RotateCcw,
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  Info,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader } from '../../ui/Card';
import Button from '../../ui/Button';
import { WithdrawalRequest } from '../../../types';
import { formatCurrency, cn } from '../../../utils';
import { useFormatDate } from '../../../hooks/useFormatDate';
import {
  getWithdrawalRequesterEmail,
  getWithdrawalRequesterId,
  getWithdrawalRequesterKind,
  getWithdrawalRequesterName,
  getWithdrawalRequesterPath,
  getWithdrawalRequesterPhone,
  getWithdrawalRequesterTypeLabel,
} from '../../../utils/withdrawalRequester';
import { getWithdrawalTypeClassName, getWithdrawalTypeLabel } from '../../../utils/withdrawalTypeDisplay';
import { getWithdrawalPayoutDestination } from '../../../utils/withdrawalPayoutDestination';
import { getWithdrawalAdminNextStep, getWithdrawalApprovalOwnerLabel } from '../../../utils/withdrawalStatusDisplay';
import {
  getRecordedFailureReason,
  shouldShowRecordedFailureBanner,
} from '../../../utils/transactionFailureReason';
import WithdrawalStatusBadge from './WithdrawalStatusBadge';
import WithdrawalReviewIndicator from './WithdrawalReviewIndicator';
import { PROVIDER_CONFIG } from '../../../constants/provider';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/shadcn/collapsible';
import { useWithdrawalPayoutLog, useRetryWithdrawalPayout } from '../../../hooks/useApi';
import { LoadingSpinner } from '../../ui/Loading';
import RejectWithdrawalModal from './RejectWithdrawalModal';
import IntegrationLogsLink from '../integrationLogs/IntegrationLogsLink';
import {
  formatWithdrawalDisplayAmount,
  getWithdrawalDisplayAmount,
  isPortalUsdWithdrawal,
} from '../../../utils/portalUsdWithdrawalDisplay';

interface WithdrawalDetailsProps {
  withdrawal: WithdrawalRequest | null;
  onClose: () => void;
  onSuccess?: () => void;
  onViewReviewAnalysis?: () => void;
}

function DetailRow({
  label,
  children,
  className,
}: {
  label: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={cn('flex flex-col gap-0.5 sm:flex-row sm:items-start sm:justify-between sm:gap-4', className)}>
      <span className="text-sm text-muted-foreground shrink-0">{label}</span>
      <div className="text-sm text-foreground sm:text-right min-w-0">{children}</div>
    </div>
  );
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return <h4 className="text-sm font-semibold text-foreground tracking-tight">{children}</h4>;
}

function SectionBlock({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="space-y-3 rounded-lg border border-border bg-muted/20 p-4">
      <SectionTitle>{title}</SectionTitle>
      <div className="space-y-2.5">{children}</div>
    </section>
  );
}

function getProviderLabel(provider?: string | null): string {
  if (!provider) return '—';
  const key = provider.toLowerCase() as keyof typeof PROVIDER_CONFIG;
  return PROVIDER_CONFIG[key]?.label ?? provider;
}

function getRequesterSubtitleRef(w: WithdrawalRequest): string {
  const name = getWithdrawalRequesterName(w);
  if (name !== '—') return name;
  const email = getWithdrawalRequesterEmail(w);
  if (email) return email;
  const phone = getWithdrawalRequesterPhone(w);
  if (phone) return phone;
  const id = getWithdrawalRequesterId(w);
  return id ?? 'unidentified requester';
}

function isWithdrawalShopRestricted(w: WithdrawalRequest): boolean {
  return !!(w.user?.shop_id ?? w.user?.shopId) || !!w.requiresShopApproval;
}

function formatUsdAmount(value?: number | string | null): string {
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  return formatCurrency(n, 'USD');
}

function getNetworkFeeSourceLabel(source?: string | null): string {
  if (source === 'transaction_snapshot') return 'Transaction snapshot';
  if (source === 'current_portal_fee_config') return 'Current portal fee config';
  if (source === 'current_provider_fee_config') return 'Current provider fee config';
  return 'Unavailable';
}

const WithdrawalDetails: React.FC<WithdrawalDetailsProps> = ({
  withdrawal,
  onClose,
  onSuccess,
  onViewReviewAnalysis,
}) => {
  const fmt = useFormatDate();
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [payoutLogOpen, setPayoutLogOpen] = useState(false);
  const { data: payoutLogData, isLoading: isLoadingPayoutLog, refetch: refetchPayoutLog } = useWithdrawalPayoutLog(
    withdrawal?.transactionId || ''
  );
  const retryPayoutMutation = useRetryWithdrawalPayout();

  if (!withdrawal) return null;

  const requesterKind = getWithdrawalRequesterKind(withdrawal);
  const requesterId = getWithdrawalRequesterId(withdrawal);
  const requesterPath = getWithdrawalRequesterPath(withdrawal);
  const requesterName = getWithdrawalRequesterName(withdrawal);
  const requesterEmail = getWithdrawalRequesterEmail(withdrawal);
  const requesterPhone = getWithdrawalRequesterPhone(withdrawal);
  const requesterTypeLabel = getWithdrawalRequesterTypeLabel(requesterKind);
  const requesterSubtitleRef = getRequesterSubtitleRef(withdrawal);
  const shopRestricted = isWithdrawalShopRestricted(withdrawal);
  const adminNextStep = getWithdrawalAdminNextStep(withdrawal.status, shopRestricted);

  const requesterIdLabel =
    requesterKind === 'super_agent'
      ? 'Super Agent ID'
      : requesterKind === 'agent'
        ? 'Agent ID'
        : requesterKind === 'shop'
          ? 'Shop ID'
          : 'User ID';

  const adminNotesText = (withdrawal.adminNotes || '').trim();
  const failureReasonText = getRecordedFailureReason({
    errorMessage: withdrawal.errorMessage,
    syncMetadata: withdrawal.syncMetadata ?? null,
  });
  const showFailureBanner = shouldShowRecordedFailureBanner(withdrawal.status, failureReasonText);
  const showAdminNotes = adminNotesText.length > 0;

  const payoutOrder = payoutLogData?.data?.payoutOrder;
  const amountValueDisplay = formatWithdrawalDisplayAmount(withdrawal);
  const amountDisplay = formatWithdrawalDisplayAmount(withdrawal, { signed: true });
  const currencyLabel = 'USD';

  const canRetry =
    withdrawal.status === 'approved' &&
    payoutOrder &&
    (payoutOrder.status === 'payoutFail' || payoutOrder.status === 'pending');

  const handleRetryPayout = async () => {
    if (!withdrawal?.transactionId) return;
    try {
      await retryPayoutMutation.mutateAsync(withdrawal.transactionId);
      setTimeout(() => {
        refetchPayoutLog();
      }, 1000);
    } catch {
      // Error is handled in the mutation
    }
  };

  const handleRejectSuccess = () => {
    setShowRejectModal(false);
    onSuccess?.();
    onClose();
  };

  const getTypeBadge = (w: WithdrawalRequest) => {
    const type = w.withdrawalType;
    const label = getWithdrawalTypeLabel(type, w);
    const className = getWithdrawalTypeClassName(type, w);
    const Icon = type === 'bank_transfer' ? Landmark : Coins;

    return (
      <span className={cn('px-2.5 py-0.5 rounded-full text-xs font-medium inline-flex items-center gap-1', className)}>
        <Icon className="h-3 w-3" />
        {label}
      </span>
    );
  };

  const renderIdValue = (id: string | null, path: string | null) => {
    if (!id) return <span className="text-muted-foreground">—</span>;
    if (path) {
      return (
        <Link to={path} className="font-mono text-xs text-primary hover:underline break-all">
          {id}
        </Link>
      );
    }
    return <span className="font-mono text-xs break-all">{id}</span>;
  };

  const approvalOwner = getWithdrawalApprovalOwnerLabel(withdrawal.status, {
    shopRestricted,
    approvedByEmail: withdrawal.approvedByEmail,
  });

  const payoutDestination = getWithdrawalPayoutDestination(withdrawal);
  const feeSnapshot = withdrawal.feeSnapshot;
  const payoutBreakdown = withdrawal.expectedPayoutBreakdown;
  const configuredProviderNetworkFee = Number(withdrawal.configuredProviderFee?.networkFeeAmount ?? 0);
  const portalUsdWithdrawal = isPortalUsdWithdrawal(withdrawal);
  const showFeeBreakdown = portalUsdWithdrawal
    ? Boolean(
        payoutBreakdown ||
          (Number.isFinite(configuredProviderNetworkFee) && configuredProviderNetworkFee > 0)
      )
    : Boolean(
        feeSnapshot ||
          payoutBreakdown ||
          (Number.isFinite(configuredProviderNetworkFee) && configuredProviderNetworkFee > 0)
      );
  const feeSectionTitle = portalUsdWithdrawal ? 'Withdrawal breakdown' : 'Payout estimate';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 !mt-0">
      <Card className="w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <CardHeader className="flex flex-row items-start justify-between gap-4 space-y-0 pb-3 border-b border-border">
          <div className="min-w-0 flex-1 space-y-2">
            <div>
              <h2 className="text-lg font-semibold text-foreground">Withdrawal request</h2>
              <p className="mt-0.5 text-sm text-muted-foreground">
                {requesterTypeLabel} request from {requesterSubtitleRef}
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {getTypeBadge(withdrawal)}
              <WithdrawalStatusBadge status={withdrawal.status} />
            </div>
          </div>
          <div className="flex flex-col items-end gap-2 shrink-0">
            <div className="flex items-center gap-1">
              {canRetry && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleRetryPayout}
                  disabled={retryPayoutMutation.isLoading}
                  className="gap-1.5"
                >
                  <RotateCcw className={cn('h-3.5 w-3.5', retryPayoutMutation.isLoading && 'animate-spin')} />
                  <span className="sr-only sm:not-sr-only sm:inline">Retry</span>
                </Button>
              )}
              <Button variant="ghost" size="sm" onClick={onClose} aria-label="Close">
                <X className="h-4 w-4" />
              </Button>
            </div>
            <p className="text-lg font-bold text-red-600 tabular-nums whitespace-nowrap">{amountDisplay}</p>
          </div>
        </CardHeader>

        <CardContent className="space-y-4 pt-4">
          {shopRestricted ? (
            <div
              role="alert"
              className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950 flex gap-3"
            >
              <AlertTriangle className="h-5 w-5 shrink-0 text-amber-600 mt-0.5" />
              <p className="leading-relaxed">
                This withdrawal belongs to a shop player. Admin can review only. Approval/rejection must be handled by
                the owning shop.
              </p>
            </div>
          ) : null}

          <WithdrawalReviewIndicator
            withdrawal={withdrawal}
            variant="banner"
            onViewFullAnalysis={onViewReviewAnalysis}
          />

          <div
            role="status"
            className="rounded-lg border border-blue-200 bg-blue-50/80 px-4 py-3 text-sm text-blue-950 flex gap-3"
          >
            <Info className="h-5 w-5 shrink-0 text-blue-600 mt-0.5" />
            <div>
              <p className="font-semibold text-blue-900">What to do next</p>
              <p className="mt-1 leading-relaxed">{adminNextStep}</p>
            </div>
          </div>

          {showFailureBanner ? (
            <div
              role="alert"
              className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-950 flex gap-3"
            >
              <AlertTriangle className="h-5 w-5 shrink-0 text-red-600 mt-0.5" />
              <div className="min-w-0">
                <p className="font-semibold text-red-900">Failure reason</p>
                <p className="mt-1 whitespace-pre-wrap break-words leading-relaxed">{failureReasonText}</p>
              </div>
            </div>
          ) : null}

          <SectionBlock title="Request summary">
            <DetailRow label="Transaction ID">
              <span className="font-mono text-xs break-all">{withdrawal.transactionId}</span>
            </DetailRow>
            <DetailRow label="Type">{getTypeBadge(withdrawal)}</DetailRow>
            <DetailRow label="Provider">
              <span>{getProviderLabel(withdrawal.provider)}</span>
            </DetailRow>
            <DetailRow label="Amount">
              <span className="font-medium tabular-nums">{amountValueDisplay}</span>
            </DetailRow>
            <DetailRow label="Currency">
              <span>{currencyLabel}</span>
            </DetailRow>
            <DetailRow label="Status">
              <WithdrawalStatusBadge status={withdrawal.status} />
            </DetailRow>
            <DetailRow label="Approval owner">
              <span>{approvalOwner}</span>
            </DetailRow>
          </SectionBlock>

          {showFeeBreakdown ? (
            <SectionBlock title={feeSectionTitle}>
              {portalUsdWithdrawal ? (
                <>
                  <DetailRow label="Total debited">
                    <span className="font-medium tabular-nums">
                      {formatUsdAmount(feeSnapshot?.tierFundingUsd ?? getWithdrawalDisplayAmount(withdrawal))}
                    </span>
                  </DetailRow>
                  <DetailRow label="Gateway processing fee">
                    <span className="tabular-nums">{formatUsdAmount(feeSnapshot?.gatewayFeeAmount)}</span>
                  </DetailRow>
                  <DetailRow label="Network fee">
                    <span className="tabular-nums">
                      {formatUsdAmount(payoutBreakdown?.expectedNetworkFeeUsd ?? feeSnapshot?.networkFeeAmount)}
                    </span>
                  </DetailRow>
                  <DetailRow label="System fee">
                    <span className="tabular-nums">{formatUsdAmount(feeSnapshot?.systemFeeAmount)}</span>
                  </DetailRow>
                  <DetailRow label="Expected net receive">
                    <span className="font-semibold tabular-nums">
                      {formatUsdAmount(payoutBreakdown?.expectedReceivedUsd)}
                    </span>
                  </DetailRow>
                </>
              ) : (
                <>
                  <DetailRow label="Sent to provider">
                    <span className="font-medium tabular-nums">
                      {formatUsdAmount(payoutBreakdown?.sentToProviderUsd ?? feeSnapshot?.gatewayPayoutUsd)}
                    </span>
                  </DetailRow>
                  <DetailRow label="System fee">
                    <span className="tabular-nums">{formatUsdAmount(feeSnapshot?.systemFeeAmount)}</span>
                  </DetailRow>
                  <DetailRow label="Expected network fee">
                    <div className="space-y-1">
                      <p className="font-medium tabular-nums">
                        {formatUsdAmount(payoutBreakdown?.expectedNetworkFeeUsd ?? feeSnapshot?.networkFeeAmount)}
                      </p>
                      {payoutBreakdown?.expectedNetworkFeeSource ? (
                        <p className="text-xs text-muted-foreground">
                          {getNetworkFeeSourceLabel(payoutBreakdown.expectedNetworkFeeSource)}
                          {payoutBreakdown.isEstimate ? ' · estimate' : ''}
                        </p>
                      ) : null}
                    </div>
                  </DetailRow>
                  <DetailRow label="Expected received balance">
                    <span className="font-semibold tabular-nums">
                      {formatUsdAmount(payoutBreakdown?.expectedReceivedUsd)}
                    </span>
                  </DetailRow>
                  <DetailRow label="Total debited">
                    <span className="tabular-nums">{formatUsdAmount(feeSnapshot?.tierFundingUsd)}</span>
                  </DetailRow>
                  {Number.isFinite(configuredProviderNetworkFee) && configuredProviderNetworkFee > 0 ? (
                    <DetailRow label={`Provider config (${withdrawal.configuredProviderFee?.methodId || 'all'})`}>
                      <span className="tabular-nums">{formatUsdAmount(configuredProviderNetworkFee)}</span>
                    </DetailRow>
                  ) : null}
                </>
              )}
            </SectionBlock>
          ) : null}

          <SectionBlock title="Requester">
            <DetailRow label="Requester type">
              <span>{requesterTypeLabel}</span>
            </DetailRow>
            <DetailRow label="Name">
              <span>{requesterName}</span>
            </DetailRow>
            <DetailRow label="Email">
              {requesterEmail && requesterPath ? (
                <Link to={requesterPath} className="text-primary hover:underline break-all">
                  {requesterEmail}
                </Link>
              ) : requesterEmail ? (
                <span className="break-all">{requesterEmail}</span>
              ) : (
                <span className="text-muted-foreground">—</span>
              )}
            </DetailRow>
            <DetailRow label="Phone">
              {requesterPhone && requesterPath ? (
                <Link to={requesterPath} className="text-primary hover:underline">
                  {requesterPhone}
                </Link>
              ) : requesterPhone ? (
                <span>{requesterPhone}</span>
              ) : (
                <span className="text-muted-foreground">—</span>
              )}
            </DetailRow>
            <DetailRow label={requesterIdLabel}>{renderIdValue(requesterId, requesterPath)}</DetailRow>
          </SectionBlock>

          <SectionBlock title="Payout destination">
            {!payoutDestination ? (
              <p className="text-sm text-muted-foreground">No payout destination on file for this request.</p>
            ) : payoutDestination.kind === 'bank' ? (
              <>
                <DetailRow label="Bank code">
                  <span>{payoutDestination.bankCode || '—'}</span>
                </DetailRow>
                <DetailRow label="Account number">
                  <span className="font-mono text-xs break-all">{payoutDestination.accountNumber || '—'}</span>
                </DetailRow>
                <DetailRow label="Account name">
                  <span>{payoutDestination.accountName || '—'}</span>
                </DetailRow>
                <DetailRow label="Routing number">
                  <span className="font-mono text-xs break-all">{payoutDestination.routingNumber || '—'}</span>
                </DetailRow>
                {payoutDestination.accountType ? (
                  <DetailRow label="Account type">
                    <span>{payoutDestination.accountType}</span>
                  </DetailRow>
                ) : null}
              </>
            ) : payoutDestination.kind === 'crypto' ? (
              <>
                <DetailRow label="Wallet address">
                  <span className="font-mono text-xs break-all">{payoutDestination.address}</span>
                </DetailRow>
                <DetailRow label="Network">
                  <span className="capitalize">{payoutDestination.network}</span>
                </DetailRow>
                {payoutDestination.memo ? (
                  <DetailRow label="Memo">
                    <span className="font-mono text-xs break-all">{payoutDestination.memo}</span>
                  </DetailRow>
                ) : null}
              </>
            ) : payoutDestination.kind === 'pyusd' ? (
              <>
                <DetailRow label="PYUSD address">
                  <span className="font-mono text-xs break-all">{payoutDestination.address}</span>
                </DetailRow>
                <DetailRow label="Network">
                  <span className="capitalize">{payoutDestination.network}</span>
                </DetailRow>
              </>
            ) : (
              <>
                <DetailRow label="ECash account">
                  <span className="font-mono text-xs break-all">{payoutDestination.account}</span>
                </DetailRow>
                {payoutDestination.accountName ? (
                  <DetailRow label="Account name">
                    <span>{payoutDestination.accountName}</span>
                  </DetailRow>
                ) : null}
              </>
            )}
          </SectionBlock>

          <SectionBlock title="Timeline">
            <DetailRow label="Created">
              <span className="inline-flex items-center gap-1">
                <Calendar className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                {fmt(withdrawal.createdAt)}
              </span>
            </DetailRow>
            <DetailRow label="Updated">
              <span className="inline-flex items-center gap-1">
                <Calendar className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                {fmt(withdrawal.updatedAt)}
              </span>
            </DetailRow>
            {withdrawal.approvedAt ? (
              <DetailRow label="Approved at">
                <span className="inline-flex items-center gap-1">
                  <CheckCircle className="h-3.5 w-3.5 text-green-600 shrink-0" />
                  {fmt(withdrawal.approvedAt)}
                </span>
              </DetailRow>
            ) : null}
            {withdrawal.approvedByEmail ? (
              <DetailRow label="Approved by">
                <span className="break-all">{withdrawal.approvedByEmail}</span>
              </DetailRow>
            ) : null}
          </SectionBlock>

          {showAdminNotes ? (
            <section className="space-y-2">
              <SectionTitle>Admin notes</SectionTitle>
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
                <p className="text-sm text-amber-950 whitespace-pre-wrap break-words leading-relaxed">{adminNotesText}</p>
              </div>
            </section>
          ) : null}

          {isLoadingPayoutLog ? (
            <div className="flex items-center justify-center h-16">
              <LoadingSpinner />
            </div>
          ) : payoutOrder ? (
            <Collapsible open={payoutLogOpen} onOpenChange={setPayoutLogOpen}>
              <div className="rounded-lg border border-border">
                <CollapsibleTrigger asChild>
                  <button
                    type="button"
                    className="flex w-full items-center gap-2 px-4 py-3 text-left text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-muted/30 transition-colors"
                    aria-expanded={payoutLogOpen}
                  >
                    {payoutLogOpen ? (
                      <ChevronDown className="h-4 w-4 shrink-0" />
                    ) : (
                      <ChevronRight className="h-4 w-4 shrink-0" />
                    )}
                    <AlertCircle className="h-4 w-4 shrink-0" />
                    <span className="flex-1">Technical payout log</span>
                    {withdrawal.transactionId ? (
                      <span onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
                        <IntegrationLogsLink tab="payment" transactionId={withdrawal.transactionId} />
                      </span>
                    ) : null}
                  </button>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <div className="border-t border-border px-4 py-3 space-y-3 text-sm bg-muted/10">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <span className="text-xs text-muted-foreground">Payout order ID</span>
                        <p className="font-mono text-xs break-all mt-0.5">{payoutOrder.payoutOrderId}</p>
                      </div>
                      <div>
                        <span className="text-xs text-muted-foreground">Status</span>
                        <p className="mt-0.5">
                          <span
                            className={cn(
                              'px-2 py-0.5 rounded text-xs font-medium',
                              payoutOrder.status === 'payoutCompleted'
                                ? 'bg-green-100 text-green-800'
                                : payoutOrder.status === 'payoutFail'
                                  ? 'bg-red-100 text-red-800'
                                  : 'bg-yellow-100 text-yellow-800'
                            )}
                          >
                            {payoutOrder.status}
                          </span>
                        </p>
                      </div>
                      <div>
                        <span className="text-xs text-muted-foreground">Created</span>
                        <p className="mt-0.5">{fmt(payoutOrder.createdAt)}</p>
                      </div>
                      <div>
                        <span className="text-xs text-muted-foreground">Retry count</span>
                        <p className="mt-0.5">{payoutOrder.retryCount}</p>
                      </div>
                      {payoutOrder.lastRetryAt ? (
                        <div>
                          <span className="text-xs text-muted-foreground">Last retry</span>
                          <p className="mt-0.5">{fmt(payoutOrder.lastRetryAt)}</p>
                        </div>
                      ) : null}
                    </div>

                    {payoutOrder.errorMessage ? (
                      <div>
                        <span className="text-xs text-muted-foreground">Error message</span>
                        <p className="mt-1 text-xs text-red-700 bg-red-50 p-2 rounded break-words">{payoutOrder.errorMessage}</p>
                      </div>
                    ) : null}

                    {payoutOrder.currentAttempt ? (
                      <div className="pt-2 border-t border-border/60 space-y-1">
                        <p className="text-xs font-medium text-muted-foreground">Current attempt</p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
                          <div>
                            <span className="text-muted-foreground">Attempt: </span>
                            <span>{payoutOrder.currentAttempt.attempt}</span>
                          </div>
                          <div>
                            <span className="text-muted-foreground">Status: </span>
                            <span>{payoutOrder.currentAttempt.status}</span>
                          </div>
                          <div>
                            <span className="text-muted-foreground">Time: </span>
                            <span>{fmt(payoutOrder.currentAttempt.timestamp)}</span>
                          </div>
                        </div>
                      </div>
                    ) : null}

                    <details className="group">
                      <summary className="cursor-pointer text-xs font-medium text-muted-foreground hover:text-foreground">
                        Request payload
                      </summary>
                      <pre className="mt-2 text-[11px] bg-background p-2 rounded border overflow-auto max-h-32 break-all">
                        {payoutOrder.requestData && Object.keys(payoutOrder.requestData).length > 0
                          ? JSON.stringify(payoutOrder.requestData, null, 2)
                          : 'No request data'}
                      </pre>
                    </details>

                    <details className="group">
                      <summary className="cursor-pointer text-xs font-medium text-muted-foreground hover:text-foreground">
                        Response payload
                      </summary>
                      <pre className="mt-2 text-[11px] bg-background p-2 rounded border overflow-auto max-h-32 break-all">
                        {payoutOrder.responseData && Object.keys(payoutOrder.responseData).length > 0
                          ? JSON.stringify(payoutOrder.responseData, null, 2)
                          : 'No response data'}
                      </pre>
                    </details>
                  </div>
                </CollapsibleContent>
              </div>
            </Collapsible>
          ) : null}

          <div className="flex justify-end pt-2 border-t border-border">
            <Button variant="outline" onClick={onClose}>
              Close
            </Button>
          </div>
        </CardContent>
      </Card>

      {showRejectModal && (
        <RejectWithdrawalModal
          withdrawal={withdrawal}
          isOpen={showRejectModal}
          onClose={() => setShowRejectModal(false)}
          onSuccess={handleRejectSuccess}
        />
      )}
    </div>
  );
};

export default WithdrawalDetails;
